import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { Box, Code, Text, useColorMode, useColorModeValue } from "@chakra-ui/react";

// Chart types the assistant is allowed to request. "mixed" is a combo chart
// where each series carries its own type (e.g. column + line). Anything else
// falls back to line.
const ALLOWED_TYPES = ["line", "area", "bar", "scatter", "mixed"];

// Per-series types valid inside a "mixed" chart. "bar" is normalized to "column"
// so combos render vertical bars (a horizontal bar would fight the line series).
const MIXED_SERIES_TYPES = ["line", "area", "column", "bar"];

// Distinct, colorblind-friendly palette reused across every AI chart.
const PALETTE = ["#3182CE", "#E53E3E", "#38A169", "#DD6B20", "#805AD5", "#319795", "#D69E2E", "#718096"];

// Guards against a degenerate/adversarial spec freezing the tab during render.
const MAX_SERIES = 12;
const MAX_POINTS = 1000;

function toNumberOrNull(v) {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

// ApexCharts writes series names and category labels into the legend/tooltip
// via innerHTML, so model-provided text must be HTML-escaped to prevent script
// injection (the spec is LLM output, influenceable by tool data / pasted text).
function escapeHtml(s) {
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

// Only accept clean color strings (hex or rgb/rgba) — model-provided colors feed
// ApexCharts' fill/stroke config, so anything else is dropped and falls back to
// the default palette.
function sanitizeColor(c) {
    if (typeof c !== "string") return null;
    const s = c.trim();
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s)) return s;
    if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\)$/i.test(s)) return s;
    return null;
}

/**
 * Parse and sanitize the assistant-provided chart spec. Returns null when the
 * payload is unusable so the caller can fall back to showing the raw text.
 */
function parseSpec(raw) {
    let spec;
    try {
        spec = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (e) {
        return null;
    }
    if (!spec || typeof spec !== "object") return null;

    const type = ALLOWED_TYPES.includes(spec.type) ? spec.type : "line";
    const isMixed = type === "mixed";

    const series = (Array.isArray(spec.series) ? spec.series : [])
        .slice(0, MAX_SERIES)
        .filter((s) => s && Array.isArray(s.data))
        .map((s, i) => {
            let seriesType = null;
            if (isMixed) {
                seriesType = MIXED_SERIES_TYPES.includes(s.type) ? s.type : "line";
                if (seriesType === "bar") seriesType = "column";
            }
            return {
                name: escapeHtml(typeof s.name === "string" && s.name ? s.name : `Série ${i + 1}`),
                data: s.data.slice(0, MAX_POINTS).map(toNumberOrNull),
                type: seriesType,
                color: sanitizeColor(s.color),
                axis: s.axis === "right" ? "right" : "left",
            };
        })
        // Drop series with no usable value — an all-null series would render an
        // empty chart yet still "pass". No series left -> caller degrades to text.
        .filter((s) => s.data.some((v) => v !== null));

    if (series.length === 0) return null;

    // An empty x array behaves differently from absent categories in ApexCharts,
    // so normalize [] -> undefined. Labels are escaped for the innerHTML sinks
    // (legend/tooltip); on the SVG-text x-axis they may show entities literally,
    // an acceptable tradeoff for weather labels that rarely contain & < >.
    const rawX = Array.isArray(spec.x) ? spec.x : [];
    const categories = rawX.length
        ? rawX.slice(0, MAX_POINTS).map((c) => (c == null ? "" : escapeHtml(String(c))))
        : undefined;

    return {
        type,
        title: typeof spec.title === "string" ? spec.title : "",
        yTitle: typeof spec.yTitle === "string" ? spec.yTitle : "",
        y2Title: typeof spec.y2Title === "string" ? spec.y2Title : "",
        stacked: Boolean(spec.stacked),
        colors: (Array.isArray(spec.colors) ? spec.colors : []).map(sanitizeColor),
        categories,
        series,
    };
}

/**
 * Renders an inline ApexCharts chart from an assistant-provided `wschart` spec.
 * Invalid/undersized specs degrade to the raw JSON so a bad payload never
 * breaks the message.
 */
export default function ChartBlock({ raw }) {
    const { colorMode } = useColorMode();
    const foreColor = useColorModeValue("#4A5568", "#A0AEC0");
    const borderColor = useColorModeValue("#E2E8F0", "#2D3748");
    const cardBg = useColorModeValue("white", "gray.900");

    const spec = useMemo(() => parseSpec(raw), [raw]);

    const chart = useMemo(() => {
        if (!spec) return null;

        const isMixed = spec.type === "mixed";
        const pointCount = spec.categories?.length || spec.series[0]?.data.length || 0;
        const isBarLike = (t) => t === "bar" || t === "column";

        // Per-series color wins, else the spec-level palette override, else default.
        const colors = spec.series.map((s, i) => s.color || spec.colors[i] || PALETTE[i % PALETTE.length]);

        // Only attach a per-series `type` for a combo chart; plain charts stay single-type.
        const series = spec.series.map((s) =>
            isMixed ? { name: s.name, type: s.type, data: s.data } : { name: s.name, data: s.data }
        );

        // Bars/columns render without a stroke line; lines/areas get a 2px stroke.
        const strokeWidth = isMixed
            ? spec.series.map((s) => (isBarLike(s.type) ? 0 : 2))
            : isBarLike(spec.type)
            ? 0
            : 2;

        // Optional secondary Y axis (mixed only): series flagged axis:"right" go on
        // an opposite axis — useful when scales differ (e.g. mm vs mm/h).
        const yLabel = { style: { fontSize: "10px" } };
        const yTitleStyle = { fontSize: "11px" };
        const rightNames = isMixed ? spec.series.filter((s) => s.axis === "right").map((s) => s.name) : [];
        const leftNames = spec.series.filter((s) => !rightNames.includes(s.name)).map((s) => s.name);
        const dualAxis = rightNames.length > 0 && leftNames.length > 0;

        const yaxis = dualAxis
            ? [
                  { seriesName: leftNames, title: spec.yTitle ? { text: spec.yTitle, style: yTitleStyle } : undefined, labels: yLabel },
                  { seriesName: rightNames, opposite: true, title: spec.y2Title ? { text: spec.y2Title, style: yTitleStyle } : undefined, labels: yLabel },
              ]
            : { title: spec.yTitle ? { text: spec.yTitle, style: yTitleStyle } : undefined, labels: yLabel };

        const options = {
            chart: {
                toolbar: { show: false },
                zoom: { enabled: false },
                stacked: spec.stacked,
                foreColor,
                fontFamily: "inherit",
                animations: { enabled: false },
            },
            theme: { mode: colorMode },
            colors,
            title: spec.title ? { text: spec.title, style: { fontSize: "13px", fontWeight: 600 } } : undefined,
            dataLabels: { enabled: false },
            stroke: { curve: "smooth", width: strokeWidth },
            // Scatter draws only markers — a size of 0 would make it invisible.
            markers: { size: spec.type === "scatter" ? 5 : 0 },
            fill: spec.type === "area" ? { type: "gradient", gradient: { opacityFrom: 0.4, opacityTo: 0.05 } } : {},
            xaxis: {
                categories: spec.categories,
                tickAmount: Math.min(pointCount || 1, 12),
                labels: { rotate: -45, rotateAlways: false, hideOverlappingLabels: true, style: { fontSize: "10px" } },
            },
            yaxis,
            legend: { show: spec.series.length > 1, position: "top", fontSize: "11px" },
            grid: { borderColor, strokeDashArray: 3 },
            tooltip: { theme: colorMode, shared: true, intersect: false },
        };

        // react-apexcharts needs a concrete base type; a combo uses "line" and each
        // series overrides with its own type.
        return { options, series, chartType: isMixed ? "line" : spec.type };
    }, [spec, colorMode, foreColor, borderColor]);

    if (!spec) {
        // Malformed spec: show it as plain text rather than crashing the message.
        return (
            <Box my={2}>
                <Text fontSize="xs" color="orange.400" mb={1}>
                    Não foi possível renderizar o gráfico.
                </Text>
                <Code display="block" whiteSpace="pre-wrap" fontSize="xs" p={2} borderRadius="md">
                    {String(raw)}
                </Code>
            </Box>
        );
    }

    return (
        <Box
            my={3}
            w="100%"
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            px={2}
            py={2}
            overflowX="auto"
        >
            <Chart options={chart.options} series={chart.series} type={chart.chartType} height={280} width="100%" />
        </Box>
    );
}
